// src/types/chat.ts
// Tipos para o sistema de Chat com funcionalidades de Reply, Delete, Arquivos Nomeados

import type { ChatCallHistoryMetadata } from "@/types/chatCall";

export interface MessageFile {
  url: string;
  originalName: string;
  displayName?: string;
  size?: number;
  mimeType?: string;
  type?: "image" | "document" | "audio" | "other";
  duration?: number; // para áudios
}

export interface ReplyToMessage {
  id: number;
  deleted: boolean;
  content: string | null;
  files: MessageFile[] | null;
  file_names: string[] | null;
  sender_id: number | null;
  sender_name: string | null;
}

export interface ReadByUser {
  id: number;
  name: string;
  url_img?: { path: string };
  isRead: boolean;
  readAt?: string;
}

export interface ChatMessage {
  id: string | number;
  content: string;
  sender_id: number;
  chat_room_id: string | number;
  created_at: string;
  updated_at?: string;
  files: MessageFile[] | string;
  file_names?: string[];
  messageType: "text" | "audio" | "file" | "mixed";
  event_type?: "message" | "call" | string;
  metadata?: ChatCallHistoryMetadata | Record<string, unknown> | null;
  chat_call_id?: string | null;
  deleted?: boolean;
  deleted_at?: string;
  reply_to_id?: number;
  reply_to?: ReplyToMessage;
  readBy?: ReadByUser[];
  totalReads?: number;
  sender?: {
    id: number;
    name: string;
    last_name?: string;
    url_img?: { path: string };
  };
}

export interface ChatRoom {
  id: string | number;
  name?: string;
  is_group: boolean;
  group_image?: string;
  admin_ids?: number[];
  users: ChatUser[];
  last_message?: ChatMessage;
  unread_count?: number;
}

export interface ChatUser {
  id: number;
  name: string;
  last_name?: string;
  url_img?: { path: string };
  email?: string;
}

export interface FileUploadItem {
  file: File;
  displayName: string;
  preview?: string;
  type: "image" | "document" | "audio" | "other";
}

// Tipos para listagem de arquivos
export interface RoomFile {
  messageId: number;
  fileIndex: number;
  url: string;
  originalName: string;
  displayName: string;
  type: string; // mime type (application/pdf, image/png, etc)
  size: number;
  category: "images" | "documents" | "others";
  sentBy: {
    id: number;
    name: string;
  };
  sentAt: string;
}

export interface RoomLink {
  messageId: number;
  url: string;
  domain: string;
  sentBy: {
    id: number;
    name: string;
  };
  sentAt: string;
  messagePreview: string;
}

// Payloads WebSocket
export interface MessageDeletedPayload {
  messageId: number;
  deletedBy: number;
  deletedAt: string;
}

export interface FileRenamedPayload {
  messageId: number;
  fileIndex: number;
  displayName: string;
}

// Constantes
export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export const FILE_CATEGORIES = {
  images: ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"],
  documents: [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
    "csv",
    "rtf",
  ],
  audio: ["mp3", "wav", "ogg", "m4a", "aac", "webm"],
} as const;

export function getFileCategory(
  filename: string,
): "image" | "document" | "audio" | "other" {
  const extension = filename.toLowerCase().split(".").pop() || "";
  const imageExtensions: readonly string[] = FILE_CATEGORIES.images;
  const documentExtensions: readonly string[] = FILE_CATEGORIES.documents;
  const audioExtensions: readonly string[] = FILE_CATEGORIES.audio;

  if (imageExtensions.includes(extension)) return "image";
  if (documentExtensions.includes(extension)) return "document";
  if (audioExtensions.includes(extension)) return "audio";
  return "other";
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
